import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { Button, Chip, Select, TextField } from '@nyce/admin-kit/ui';
import {
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useSort,
  type SortKeys,
} from '../../components/common';
import { stats, type Organization } from '../../api/stats';
import { formatRelative } from '../../lib/format';
import { authCustomerOf, useStatsLinks } from './links';
import { IngestStatus, orgPath, useParam } from './shared';
import { NewOrganizationDialog } from './NewOrganization';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import './statistics.css';

const PAGE = 100;

/** Sorts the page shown (100 rows; the collector pages by name). */
const ORG_SORT: SortKeys<Organization> = {
  name: { label: 'Organization', value: (o) => o.name || `Customer ${o.customerId}` },
  status: { label: 'Status', value: (o) => o.status },
  installations: { label: 'Installations', value: (o) => o.installations, desc: true },
  location: { label: 'Location', value: (o) => o.location },
  performance: { label: 'Last performance', value: (o) => o.lastReceived.performance, desc: true },
  logs: { label: 'Last log', value: (o) => o.lastReceived.logs, desc: true },
  daily: { label: 'Last daily metric', value: (o) => o.lastReceived.dailyMetrics, desc: true },
};

/** Organizations = collector customer rows (PMS customer ids): status and when data last arrived. */
export function OrganizationsPage() {
  const [q, setQ] = useParam('q', '');
  const [active, setActive] = useParam<'on' | 'off' | 'all'>('monitoring', 'on');
  const [page, setPage] = useParam('page', '1');
  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);
  const offset = (Math.max(1, Number(page) || 1) - 1) * PAGE;
  const list = useAsync(
    () =>
      stats.organizations({
        search: q || undefined,
        active: active === 'all' ? undefined : active === 'on',
        offset,
        limit: PAGE,
      }),
    [q, active, offset]
  );
  const links = useStatsLinks();
  const sort = useSort(list.data?.items, ORG_SORT, useParam('sort', ''));
  const items = sort.rows;
  const canAdmin = hasScope(useHubLogin()?.scopes, SCOPES.statsAdmin);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  return (
    <Page
      title="Organizations"
      description="Organizations the datacollector knows (PMS customer ids): ingest status and last data received."
      actions={
        canAdmin && (
          <Button onClick={() => setCreating(true)} data-testid="new-org">
            New organization
          </Button>
        )
      }
    >
      <NewOrganizationDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(o) => navigate(orgPath(o.customerId, 'monitoring'))}
      />
      <form
        className="sx-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(draft.trim());
          setPage('1');
        }}
      >
        <TextField
          aria-label="Search"
          placeholder="Name or customer id"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Select
          aria-label="Monitoring"
          value={active}
          onChange={(v) => {
            setActive(v as 'on' | 'off' | 'all');
            setPage('1');
          }}
          options={[
            { value: 'on', label: 'Monitoring on' },
            { value: 'off', label: 'Monitoring off' },
            { value: 'all', label: 'All' },
          ]}
        />
        <Button type="submit" variant="ghost">
          Search
        </Button>
        <SortSelect sort={sort} />
      </form>
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="orgs-table">
            <thead>
              <tr>
                <SortTh sort={sort} by="name" />
                <SortTh sort={sort} by="status" />
                <SortTh sort={sort} by="installations" className="sx-num" />
                <SortTh sort={sort} by="location" />
                <SortTh sort={sort} by="performance" />
                <SortTh sort={sort} by="logs" />
                <SortTh sort={sort} by="daily" />
                <th>AUTH customer</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => {
                const customer = authCustomerOf(links, o.customerId);
                return (
                  <tr key={o.customerId} data-org={o.customerId}>
                    <td className="nyk-card-title">
                      <Link to={orgPath(o.customerId)} className="nyk-link">
                        {o.name || `Customer ${o.customerId}`}
                      </Link>
                      <div className="nyk-ui-hint">
                        id {o.customerId}
                        {!o.hasToken && ' · no token'}
                      </div>
                    </td>
                    <td data-label="Status">
                      <IngestStatus status={o.status} monitoring={o.active} />
                    </td>
                    <td data-label="Installations" className="sx-num">
                      {o.installations}
                      {o.silentInstallations > 0 && (
                        <span className="nyk-text-red"> ({o.silentInstallations} silent)</span>
                      )}
                    </td>
                    <td data-label="Location">{o.location || '—'}</td>
                    <td data-label="Last performance">{formatRelative(o.lastReceived.performance)}</td>
                    <td data-label="Last log">{formatRelative(o.lastReceived.logs)}</td>
                    <td data-label="Last daily metric">{formatRelative(o.lastReceived.dailyMetrics)}</td>
                    <td data-label="AUTH customer">
                      {customer ? (
                        <Chip size="sm">{customer.name}</Chip>
                      ) : (
                        <span className="nyk-ui-hint">{links.loading ? '…' : 'not linked'}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroller>
        {list.data && !items.length && (
          <EmptyState title="No organizations match." hint={q ? 'Try another name or id.' : undefined} />
        )}
        {list.data && (offset > 0 || list.data.hasMore) && (
          <div className="nyk-pager" data-testid="pager">
            <Button
              size="sm"
              variant="ghost"
              disabled={offset === 0}
              onClick={() => setPage(String(Number(page) - 1))}
            >
              Previous
            </Button>
            <span className="nyk-pager-text">
              {offset + 1}–{offset + items.length}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={!list.data.hasMore}
              onClick={() => setPage(String(Number(page) + 1))}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </Page>
  );
}
